import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useDistributor } from '../lib/auth';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

const MSG91_SCRIPT_SRC = 'https://verify.msg91.com/otp-provider.js';

// Load MSG91's hosted widget script exactly once per page load.
function loadMsg91Script() {
    if (typeof window === 'undefined') return Promise.resolve();
    if (window.__sdf_msg91_loaded) return Promise.resolve();
    if (window.__sdf_msg91_loading) return window.__sdf_msg91_loading;
    window.__sdf_msg91_loading = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${MSG91_SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => { window.__sdf_msg91_loaded = true; resolve(); });
            existing.addEventListener('error', reject);
            return;
        }
        const s = document.createElement('script');
        s.src = MSG91_SCRIPT_SRC;
        s.async = true;
        s.onload = () => { window.__sdf_msg91_loaded = true; resolve(); };
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return window.__sdf_msg91_loading;
}

export default function Login() {
    const nav = useNavigate();
    const loc = useLocation();
    const { signIn } = useDistributor();
    const [step, setStep] = useState('mobile');
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [resendIn, setResendIn] = useState(0);
    const [widgetReady, setWidgetReady] = useState(false);
    const [countryCode, setCountryCode] = useState('91');
    const reqIdRef = useRef(null);

    // Load config + hosted MSG91 script and init the widget.
    useEffect(() => {
        (async () => {
            try {
                const cfg = await api.get('/catalog/otp-widget-config');
                const { widget_id, token_auth, country_code, enabled } = cfg.data || {};
                if (!enabled || !widget_id || !token_auth) {
                    setError('OTP service is not configured. Please contact support.');
                    return;
                }
                setCountryCode(country_code || '91');
                await loadMsg91Script();
                if (typeof window.initSendOTP !== 'function') {
                    setError('OTP service failed to load. Please refresh the page.');
                    return;
                }
                window.initSendOTP({
                    widgetId: widget_id,
                    tokenAuth: token_auth,
                    exposeMethods: true,
                    success: () => { /* handled per-call via promise wrapper below */ },
                    failure: () => { /* handled per-call via promise wrapper below */ },
                });
                setWidgetReady(true);
            } catch (e) {
                console.error('MSG91 widget init failed', e);
                setError('Unable to load OTP service. Please try again.');
            }
        })();
    }, []);

    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setTimeout(() => setResendIn(r => r - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn]);

    const preflightLogin = async (mob) => {
        try {
            await api.post('/dist/auth/preflight-login', { mobile: mob });
            return true;
        } catch (e) {
            const msg = e.response?.data?.detail || 'Could not verify mobile number.';
            setError(msg);
            if (msg.toLowerCase().includes('not registered')) {
                setTimeout(() => nav('/register', { state: { mobile: mob } }), 1200);
            }
            return false;
        }
    };

    const sendOtp = async () => {
        setError('');
        const m = mobile.trim();
        if (!/^\d{10}$/.test(m)) { setError('Enter a valid 10-digit mobile number'); return; }
        if (!widgetReady || typeof window.sendOtp !== 'function') {
            setError('OTP service is not ready yet. Please wait a moment.'); return;
        }
        setBusy(true);
        const ok = await preflightLogin(m);
        if (!ok) { setBusy(false); return; }

        const identifier = `${countryCode}${m}`;
        try {
            const resp = await new Promise((resolve, reject) => {
                try {
                    // window.sendOtp(identifier, success, failure)
                    window.sendOtp(identifier, resolve, reject);
                } catch (err) { reject(err); }
            });
            // Widget returns reqId that we must use for verifyOtp.
            reqIdRef.current = resp?.message || resp?.reqId || resp?.request_id || null;
            setStep('otp');
            setResendIn(30);
        } catch (e) {
            const msg = (e && (e.message || e.type)) || 'Could not send OTP. Please try again.';
            setError(String(msg));
        } finally {
            setBusy(false);
        }
    };

    const verify = async () => {
        setError('');
        const code = otp.trim();
        if (!/^\d{4,8}$/.test(code)) { setError('Enter the OTP you received.'); return; }
        if (!widgetReady || typeof window.verifyOtp !== 'function') {
            setError('OTP service is not ready.'); return;
        }
        setBusy(true);
        try {
            const resp = await new Promise((resolve, reject) => {
                try {
                    // window.verifyOtp(otp, success, failure, reqId?)
                    // Some MSG91 revisions accept (otp, success, failure); others need reqId as 4th arg.
                    if (reqIdRef.current) {
                        window.verifyOtp(code, resolve, reject, reqIdRef.current);
                    } else {
                        window.verifyOtp(code, resolve, reject);
                    }
                } catch (err) { reject(err); }
            });
            const accessToken =
                resp?.message ||
                resp?.accessToken ||
                resp?.['access-token'] ||
                resp?.data?.accessToken;
            if (!accessToken) throw new Error('OTP verification succeeded but no token was returned.');

            const r = await api.post('/dist/auth/widget/verify-token', { access_token: accessToken, purpose: 'login' });
            signIn(r.data.access_token, r.data.distributor);
            const dest = loc.state?.from || '/';
            nav(dest, { replace: true });
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = detail || (e && (e.message || e.type)) || 'OTP verification failed.';
            setError(String(msg));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-sdf-ivory flex flex-col">
            <div className="px-4 py-5 flex items-center justify-center border-b border-sdf-line bg-sdf-ivory">
                <Logo variant="login" />
            </div>
            <main className="flex-1 flex items-start justify-center px-4 pt-10">
                <div className="w-full max-w-sm">
                    <h1 className="text-2xl font-extrabold text-sdf-text">Distributor Sign In</h1>
                    <p className="mt-1 text-sm text-sdf-text-2">Enter your registered mobile number to receive an OTP.</p>

                    {step === 'mobile' && (
                        <div className="mt-8 card-soft p-5">
                            <label className="text-xs font-bold text-sdf-text-2 tracking-wider">MOBILE NUMBER</label>
                            <div className="mt-2 flex items-center border border-sdf-line rounded-xl overflow-hidden bg-white">
                                <span className="px-3 py-3 text-sdf-text-2 border-r border-sdf-line select-none">+{countryCode}</span>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                                    placeholder="10-digit mobile"
                                    className="flex-1 px-3 py-3 outline-none text-sm bg-transparent"
                                    data-testid="login-mobile-input"
                                />
                            </div>
                            {error && <div className="mt-3 text-xs text-red-700" data-testid="login-error">{error}</div>}
                            <button onClick={sendOtp} disabled={busy || !widgetReady} className="btn-red-lg mt-4" data-testid="send-otp-btn">
                                {busy ? 'Sending…' : (widgetReady ? 'Send OTP' : 'Loading OTP service…')}
                            </button>
                            <div className="mt-4 text-xs text-center text-sdf-text-2">
                                New distributor? <Link to="/register" className="text-sdf-red font-bold">Register</Link>
                            </div>
                        </div>
                    )}

                    {step === 'otp' && (
                        <div className="mt-6 card-soft p-5">
                            <div className="flex items-center gap-2">
                                <button onClick={() => { setStep('mobile'); setOtp(''); setError(''); }} className="p-2 rounded-full hover:bg-black/5" aria-label="back"><ArrowLeft size={18} /></button>
                                <div className="text-xs text-sdf-text-2">Sent to <span className="font-bold text-sdf-text">+{countryCode} {mobile}</span></div>
                            </div>
                            <label className="mt-4 block text-xs font-bold text-sdf-text-2 tracking-wider">ENTER OTP</label>
                            <input
                                type="tel"
                                inputMode="numeric"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                placeholder="6-digit code"
                                className="mt-2 w-full border border-sdf-line rounded-xl px-4 py-3 text-lg font-bold tracking-widest bg-white outline-none"
                                data-testid="login-otp-input"
                            />
                            {error && <div className="mt-3 text-xs text-red-700" data-testid="otp-error">{error}</div>}
                            <button onClick={verify} disabled={busy} className="btn-red-lg mt-4" data-testid="verify-otp-btn">{busy ? 'Verifying…' : 'Verify & Sign In'}</button>
                            <div className="mt-4 text-xs text-center text-sdf-text-2">
                                {resendIn > 0 ? <span>Resend OTP in {resendIn}s</span> : <button onClick={sendOtp} className="text-sdf-red font-bold">Resend OTP</button>}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
